import { FormControl, Grid, Select, MenuItem, InputLabel, TextField, InputAdornment, IconButton, ListSubheader } from "@mui/material";

import React, { memo, useRef, useEffect, useState } from "react";

import {
	flexCenterProps,
	menuItemStyle,
	paddingX1Props,
	requiredLabelStyle,
	selectMenuPropStyle,
} from "@styles/global-css-class";
import { FieldError } from "@components/FieldError";
import { IDropdownFormControlProperty } from "@definitions/index";
import { StyledFormLabel } from "..";
import { colors } from "@components/ThemeRegistry/theme";
import DiffInheritanceIndicator from "@components/DiffInheritanceIndicator";

function NewDropdownFormControl({
	childKey,
	label,
	name,
	value,
	defaultValue,
	onChange,
	isRequired,
	isDisabled,
	isHidden,
	showSelect = true,
	listValues,
	placeHolder,
	formState,
	zIndex,
	xsFormControl = 12,
	mdFormControl = 12,
	xsLabel = 0,
	xsInput,
	textAlign,
	textErrorAlign,
	itemSx,
	textSx,
	errorName,
	isExpanded,
	allowUndefinedDefaultSelect = false,
	isDisableDefaultSelect,
	inputSx,
	isLabelToolTip,
	labelToolTip,
	isHideLabel = true,
	valueDefaultSelect,
	isShowRequiredAsterisk = true,
	showIndicator,
	isIndicatorTakeSpace,
	showSearchBox,
	...props
}: Readonly<IDropdownFormControlProperty>) {
	const inputRef = useRef<HTMLInputElement>(null);
	const [valueFilter, setValueFilter] = useState("");
	const [listItems, setListItems] = useState<any[]>(listValues);

	useEffect(() => {
		if (isExpanded && inputRef.current) {
			inputRef.current?.focus();
		}
	}, [isExpanded, inputRef.current]);

	useEffect(() => {
		setListItems(listValues ?? []);
	}, [listValues])

	const handleFilterChange = (event: any) => {
		setValueFilter(event.target.value);
		setListItems(getFilteredOptions(event.target.value));
	};

	const handleClearFilter = () => {
		setValueFilter('');
		setListItems(getFilteredOptions(''));
	};

	const getFilteredOptions = (filterText: string) => {
		if (!filterText) {
			return listValues || [];
		}

		filterText = filterText.toLowerCase();
		return listValues.filter((option: any) => {
			return option.value?.toLowerCase().includes(filterText)
				|| option.name?.toLowerCase().includes(filterText);
		});
	}

	return <Grid container item xs={xsFormControl} md={mdFormControl} sx={itemSx ?? {
		...paddingX1Props,
		alignContent: 'flex-start'
	}}>
		<FormControl key={`${childKey}-form-control`} fullWidth margin='normal' {...props} >
			<Grid container item sx={flexCenterProps}>
				{!isHideLabel && <Grid item xs={xsLabel} sx={itemSx ?? { pr: 1 }}>
					<StyledFormLabel
						aria-describedby={name}
						hidden={isHidden}
						error={formState?.errors[errorName ?? name] !== undefined}
						sx={textSx ?? {
							...requiredLabelStyle,
							justifyContent: textAlign ?? "right",
						}}>
						{label}
						{isRequired && isShowRequiredAsterisk && ' *'}
					</StyledFormLabel>
				</Grid>}
				<Grid container item xs={xsInput ?? (xsLabel === 12 ? 12 : 12 - xsLabel)} sx={{ position: "relative" }} flexWrap={'nowrap'}>
					<Grid item xs={isIndicatorTakeSpace?11:12}>
						{isHideLabel && <InputLabel
							key={`${childKey}-form-control-input-label`}
							id={name}
							sx={textSx ?? {}}
							error={formState?.errors[errorName ?? name] !== undefined}
						>
							{label}
							{isRequired && ' *'}
						</InputLabel>}
						<Select
							key={`${childKey}-form-control-select`}
							labelId={isHideLabel ? name : undefined}
							id={name}
							name={name}
							label={isHideLabel ? `${label}${isRequired && isShowRequiredAsterisk ? ' *' : ''} ` : undefined}
							required={isRequired}
							hidden={isHidden}
							value={defaultValue ?? value ?? ' '}
							error={formState?.errors[errorName ?? name] !== undefined}
							inputRef={inputRef}
							inputProps={{
								sx: {
									backgroundColor: colors.white,
								}
							}}
							onChange={(e) => {
								onChange(e.target.value === ' ' ? null : e.target.value);
							}}
							sx={inputSx ?? { width: "100%", }}
							disabled={isDisabled}
							onClose={() => handleClearFilter()}
							MenuProps={
								{
									...selectMenuPropStyle(zIndex ?? 99999),
									autoFocus: !showSearchBox
								}
							}
						>
							{showSearchBox === true
								&& <ListSubheader sx={{ height: '40px' }}>
									<TextField
										value={valueFilter}
										size="small"
										// Autofocus on textfield
										autoFocus
										placeholder="Type to search..."
										fullWidth
										InputProps={{
											endAdornment: (
												<InputAdornment position="end">
													<IconButton
														aria-label="clear search"
														onClick={handleClearFilter}
													>
														x
													</IconButton>
												</InputAdornment>
											)
										}}
										onChange={handleFilterChange}
										onKeyDown={(e) => {
											if (e.key !== "Escape") {
												// Prevents auto selecting item while typing (default Select behavior)
												e.stopPropagation();
											}
										}}
									/>
								</ListSubheader>
							}
							{
								showSelect && <MenuItem
									disabled={isDisableDefaultSelect}
									sx={{ ...menuItemStyle, fontStyle: "italic" }}
									key={`${childKey}-9999-null`}
									value={allowUndefinedDefaultSelect ? undefined : valueDefaultSelect ?? ' '}
								>
									{placeHolder ?? `Select ${label}`}
								</MenuItem>
							}
							{listItems?.map((option, idx) => {
								idx += 1;
								return typeof option === "object" ? <MenuItem
									disabled={option?.disabled ?? false}
									sx={menuItemStyle}
									key={`${childKey}-${idx}-${option.value}`}
									value={option.value}>
									{option.name}
								</MenuItem> : <MenuItem
									sx={menuItemStyle}
									key={`${childKey}-${idx}-${option}`}
									value={option}>
									{option}
								</MenuItem>;
							})}
						</Select>
						{formState && <FieldError key={`${childKey}-form-control-error`} formState={formState} name={errorName ?? name} textAlign={textErrorAlign} />}
					</Grid>
					{showIndicator !== undefined && showIndicator() == true &&
						<Grid item xs={isIndicatorTakeSpace?1:0} sx={{ ...flexCenterProps }}>
							<DiffInheritanceIndicator />
						</Grid>
					}

				</Grid>

			</Grid>
			
		</FormControl>
	</Grid >;
}

export default memo(NewDropdownFormControl);
